import { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  Field,
  Modal,
  PrimaryButton,
  SecondaryButton,
  TextInput,
  TextArea,
  ErrorBanner,
} from "../crm/Form";
import {
  ALERT_TYPES,
  SEVERITIES,
  VENDORS,
  createRule,
  updateRule,
} from "../../lib/monitoring";
import { formatApiError } from "../../lib/api";

const EMPTY = {
  name: "",
  conditions: { severity_in: [], alert_type_in: [], vendor_in: [] },
  actions: {
    create_service_request: false,
    notify_ops: false,
    notification_message: "",
  },
};

export default function RuleFormModal({ open, onClose, rule, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      if (rule) {
        setForm({
          name: rule.name || "",
          conditions: {
            severity_in: rule.conditions?.severity_in || [],
            alert_type_in: rule.conditions?.alert_type_in || [],
            vendor_in: rule.conditions?.vendor_in || [],
          },
          actions: {
            create_service_request: rule.actions?.create_service_request || false,
            notify_ops: rule.actions?.notify_ops || false,
            notification_message: rule.actions?.notification_message || "",
          },
        });
      } else {
        setForm(EMPTY);
      }
      setErr("");
    }
  }, [open, rule]);

  const toggleIn = (field, value) => {
    setForm((f) => {
      const arr = f.conditions[field] || [];
      const next = arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
      return { ...f, conditions: { ...f.conditions, [field]: next } };
    });
  };

  const submit = async () => {
    setErr("");
    if (!form.name.trim()) {
      setErr("Rule name is required.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        conditions: {
          severity_in:
            form.conditions.severity_in.length > 0
              ? form.conditions.severity_in
              : null,
          alert_type_in:
            form.conditions.alert_type_in.length > 0
              ? form.conditions.alert_type_in
              : null,
          vendor_in:
            form.conditions.vendor_in.length > 0 ? form.conditions.vendor_in : null,
        },
        actions: {
          create_service_request: form.actions.create_service_request,
          notify_ops: form.actions.notify_ops,
          notification_message:
            form.actions.notification_message.trim() || null,
        },
      };
      if (rule) {
        await updateRule(rule.id, body);
      } else {
        await createRule(body);
      }
      toast.success(rule ? "Rule saved" : "Rule created");
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !busy && onClose && onClose()}
      title={rule ? "Edit rule" : "New rule"}
      subtitle="automation"
      data-testid="rule-form-modal"
    >
      <div className="space-y-4">
        <ErrorBanner message={err} />
        <Field label="Rule name" required>
          <TextInput
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            data-testid="rule-name"
            maxLength={120}
            placeholder="e.g. Auto-ticket battery failures"
          />
        </Field>

        <fieldset className="rounded-md border border-slate-200 p-3">
          <legend className="px-1 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            Conditions (leave empty = match all)
          </legend>
          <CheckboxGroup
            label="Severity"
            options={SEVERITIES}
            value={form.conditions.severity_in}
            onToggle={(v) => toggleIn("severity_in", v)}
            testidPrefix="rule-severity"
          />
          <CheckboxGroup
            label="Alert types"
            options={ALERT_TYPES}
            value={form.conditions.alert_type_in}
            onToggle={(v) => toggleIn("alert_type_in", v)}
            testidPrefix="rule-alert-type"
          />
          <CheckboxGroup
            label="Vendors"
            options={VENDORS}
            value={form.conditions.vendor_in}
            onToggle={(v) => toggleIn("vendor_in", v)}
            testidPrefix="rule-vendor"
          />
        </fieldset>

        <fieldset className="rounded-md border border-slate-200 p-3 space-y-2">
          <legend className="px-1 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            Actions
          </legend>
          <Toggle
            label="Auto-create service request"
            checked={form.actions.create_service_request}
            onChange={(v) =>
              setForm({
                ...form,
                actions: { ...form.actions, create_service_request: v },
              })
            }
            testid="rule-action-create-sr"
          />
          <Toggle
            label="Notify ops team via email + SMS"
            checked={form.actions.notify_ops}
            onChange={(v) =>
              setForm({ ...form, actions: { ...form.actions, notify_ops: v } })
            }
            testid="rule-action-notify"
          />
          {form.actions.notify_ops && (
            <Field label="Notification message prefix (optional)" hint="Max 200 chars">
              <TextArea
                value={form.actions.notification_message}
                onChange={(v) =>
                  setForm({
                    ...form,
                    actions: { ...form.actions, notification_message: v },
                  })
                }
                rows={2}
                maxLength={200}
                data-testid="rule-action-message"
              />
            </Field>
          )}
        </fieldset>

        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose} disabled={busy}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="button" onClick={submit} disabled={busy} data-testid="rule-submit">
            {busy ? "Saving…" : rule ? "Save changes" : "Create rule"}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
}

function CheckboxGroup({ label, options, value, onToggle, testidPrefix }) {
  return (
    <div className="mb-2 last:mb-0">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const active = value.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onToggle(o.value)}
              data-testid={`${testidPrefix}-${o.value}`}
              className={`text-xs px-2.5 py-1 rounded-md border transition ${
                active
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2.5 text-sm cursor-pointer">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="rounded border-slate-300 text-slate-900 focus:ring-amber-500"
      />
      <span className="text-slate-800">{label}</span>
    </label>
  );
}
